export type FormVisibility = 'public' | 'workspace' | 'password_protected';
export type PublishTiming = 'immediate' | 'scheduled';

/**
 * Values are hours as strings for dropdown binding.
 * `0` = never save respondent drafts. Default is 24 hours.
 */
export type FormSubmissionDraftRetentionOption =
  | '0'
  | '24'
  | '48'
  | '168'
  | '336'
  | '720';

export const DEFAULT_FORM_SUBMISSION_DRAFT_RETENTION_HOURS = 24;

export const FORM_SUBMISSION_DRAFT_RETENTION_OPTIONS: ReadonlyArray<{
  value: FormSubmissionDraftRetentionOption;
  label: string;
}> = [
  { value: '0', label: "Don't save drafts" },
  { value: '24', label: '24 hours' },
  { value: '48', label: '48 hours' },
  { value: '168', label: '7 days' },
  { value: '336', label: '14 days' },
  { value: '720', label: '30 days' },
];

export interface FormPublishConfig {
  customSlug: string;
  visibility: FormVisibility;
  accessPassword: string;
  /** Contact IDs allowed when visibility is `workspace`. */
  allowedContactIds: string[];
  /** Group IDs allowed when visibility is `workspace`. */
  allowedGroupIds: string[];
  /** Legacy invite chips; invite-by-email now resolves to contacts via allowedContactIds. */
  invitedEmails: string[];
  publishTiming: PublishTiming;
  scheduledAt: string;
  requireSubmissionApproval: boolean;
  closeOnScheduleEnd: boolean;
  scheduledEndAt: string;
  maxSubmissions: string;
  /** Hours preset. `0` = drafts off. Default 24. Max 720 (30 days). */
  formSubmissionDraftRetentionHours: FormSubmissionDraftRetentionOption;
  /** Standard (prefix + digits) or Custom (token pattern). Always generated. */
  submissionReferenceMode: SubmissionReferenceMode;
  /** Optional prefix before the dash in Standard mode, e.g. `REF`. Empty = digits only. */
  submissionReferencePrefix: string;
  /** Zero-padded digit length for the sequence portion (Standard). */
  submissionReferenceDigitLength: SubmissionReferenceDigitLength;
  /** Custom pattern with handlebars chips, e.g. `REF-{{ref.seq6[Seq 6]}}`. */
  submissionReferencePattern: string;
}

export const SUBMISSION_REFERENCE_PREFIX_MAX = 20;
export const SUBMISSION_REFERENCE_PATTERN_MAX = 80;

export type SubmissionReferenceMode = 'standard' | 'custom';

export type SubmissionReferenceDigitLength =
  | '4'
  | '5'
  | '6'
  | '7'
  | '8';

export const DEFAULT_SUBMISSION_REFERENCE_DIGIT_LENGTH: SubmissionReferenceDigitLength =
  '6';

export const DEFAULT_SUBMISSION_REFERENCE_PATTERN =
  'REF-{{ref.seq6[Seq 6]}}';

export const SUBMISSION_REFERENCE_DIGIT_LENGTH_OPTIONS: ReadonlyArray<{
  value: SubmissionReferenceDigitLength;
  label: string;
}> = [
  { value: '4', label: '4 digits' },
  { value: '5', label: '5 digits' },
  { value: '6', label: '6 digits' },
  { value: '7', label: '7 digits' },
  { value: '8', label: '8 digits' },
];

/** Chips shown in the custom pattern builder (handlebars `{{ref.key[Label]}}`). */
export const SUBMISSION_REFERENCE_TOKEN_CHIPS: ReadonlyArray<{
  label: string;
  key: string | null;
  insert: string;
}> = [
  { label: 'Year', key: 'yyyy', insert: '{{ref.yyyy[Year]}}' },
  { label: 'YY', key: 'yy', insert: '{{ref.yy[YY]}}' },
  { label: 'Month', key: 'MM', insert: '{{ref.MM[Month]}}' },
  { label: 'Day', key: 'dd', insert: '{{ref.dd[Day]}}' },
  { label: 'Seq 4', key: 'seq4', insert: '{{ref.seq4[Seq 4]}}' },
  { label: 'Seq 5', key: 'seq5', insert: '{{ref.seq5[Seq 5]}}' },
  { label: 'Seq 6', key: 'seq6', insert: '{{ref.seq6[Seq 6]}}' },
  { label: 'Seq 7', key: 'seq7', insert: '{{ref.seq7[Seq 7]}}' },
  { label: 'Seq 8', key: 'seq8', insert: '{{ref.seq8[Seq 8]}}' },
  { label: '-', key: null, insert: '-' },
];

export function submissionReferenceDigitLengthFromValue(
  value: unknown,
): SubmissionReferenceDigitLength {
  const asString = String(value ?? '') as SubmissionReferenceDigitLength;
  const allowed: SubmissionReferenceDigitLength[] = [
    '4',
    '5',
    '6',
    '7',
    '8',
  ];
  return allowed.includes(asString)
    ? asString
    : DEFAULT_SUBMISSION_REFERENCE_DIGIT_LENGTH;
}

export function submissionReferenceModeFromValue(
  value: unknown,
): SubmissionReferenceMode {
  return value === 'custom' ? 'custom' : 'standard';
}

/**
 * Live preview for Standard mode, e.g. `REF-000123`.
 * Empty prefix → digits only (`000123`).
 */
export function formatSubmissionReferencePreview(
  prefix: string,
  digitLength: SubmissionReferenceDigitLength | string,
  sampleSequence = 1,
): string {
  const digits = submissionReferenceDigitLengthFromValue(digitLength);
  const width = Number.parseInt(digits, 10);
  const padded = padSequence(sampleSequence, width);
  const normalizedPrefix = prefix.trim().toUpperCase();
  return normalizedPrefix ? `${normalizedPrefix}-${padded}` : padded;
}

/** Count of sequence tokens (`{{ref.seqN}}` or legacy `{seq:N}`). */
export function countSubmissionReferenceSeqTokens(pattern: string): number {
  const handlebars = [...pattern.matchAll(/\{\{ref\.seq([4-8])(?:\[[^\]]*\])?\}\}/g)]
    .length;
  const legacy = [...pattern.matchAll(/\{seq:([4-8])\}/g)].length;
  return handlebars + legacy;
}

export function hasValidSubmissionReferenceSeqToken(pattern: string): boolean {
  return countSubmissionReferenceSeqTokens(pattern) === 1;
}

/** Pad to at least `width` digits; never truncate (collision-safe). */
function padSequence(sampleSequence: number, width: number): string {
  const safeSequence = Math.max(0, Math.floor(sampleSequence));
  return String(safeSequence).padStart(width, '0');
}

/**
 * Resolve a custom pattern for live preview.
 * Supports handlebars `{{ref.key[Label]}}` and legacy `{yyyy}` / `{seq:6}`.
 */
export function formatCustomSubmissionReferencePreview(
  pattern: string,
  sampleDate: Date = new Date(),
  sampleSequence = 123,
): string {
  const raw = pattern.trim() || DEFAULT_SUBMISSION_REFERENCE_PATTERN;
  const yyyy = String(sampleDate.getFullYear());
  const yy = yyyy.slice(-2);
  const mm = String(sampleDate.getMonth() + 1).padStart(2, '0');
  const dd = String(sampleDate.getDate()).padStart(2, '0');

  const resolveKey = (key: string): string | null => {
    if (key === 'yyyy') {
      return yyyy;
    }
    if (key === 'yy') {
      return yy;
    }
    if (key === 'MM') {
      return mm;
    }
    if (key === 'dd') {
      return dd;
    }
    const seqMatch = /^seq([4-8])$/.exec(key);
    if (seqMatch) {
      return padSequence(sampleSequence, Number.parseInt(seqMatch[1], 10));
    }
    return null;
  };

  let out = raw.replace(
    /\{\{ref\.([A-Za-z0-9_]+)(?:\[[^\]]*\])?\}\}/g,
    (full, key: string) => resolveKey(key) ?? full,
  );

  // Legacy short tokens from the first custom-format pass.
  out = out.replace(/\{([a-zA-Z]+)(?::(\d))?\}/g, (full, name: string, widthRaw?: string) => {
    if (name === 'seq') {
      const width = Number.parseInt(widthRaw ?? '', 10);
      if (width >= 4 && width <= 8) {
        return padSequence(sampleSequence, width);
      }
      return full;
    }
    return resolveKey(name) ?? full;
  });

  return out;
}

/** Build a default custom pattern from Standard prefix + digits. */
export function defaultCustomSubmissionReferencePattern(
  prefix: string,
  digitLength: SubmissionReferenceDigitLength | string,
): string {
  const digits = submissionReferenceDigitLengthFromValue(digitLength);
  const normalizedPrefix = prefix.trim().toUpperCase();
  const seqToken = `{{ref.seq${digits}[Seq ${digits}]}}`;
  return normalizedPrefix ? `${normalizedPrefix}-${seqToken}` : seqToken;
}

export function resolveSubmissionReferencePreview(
  config: Pick<
    FormPublishConfig,
    | 'submissionReferenceMode'
    | 'submissionReferencePrefix'
    | 'submissionReferenceDigitLength'
    | 'submissionReferencePattern'
  >,
  sampleSequence = 123,
  sampleDate: Date = new Date(),
): string {
  if (config.submissionReferenceMode === 'custom') {
    return formatCustomSubmissionReferencePreview(
      config.submissionReferencePattern,
      sampleDate,
      sampleSequence,
    );
  }
  return formatSubmissionReferencePreview(
    config.submissionReferencePrefix,
    config.submissionReferenceDigitLength,
    sampleSequence,
  );
}

/** Client-side publish gate for custom patterns. */
export function getSubmissionReferencePublishError(
  config: Pick<
    FormPublishConfig,
    | 'submissionReferenceMode'
    | 'submissionReferencePrefix'
    | 'submissionReferencePattern'
  >,
): string | null {
  const prefix = config.submissionReferencePrefix.trim();
  if (prefix.length > SUBMISSION_REFERENCE_PREFIX_MAX) {
    return `Prefix must be at most ${SUBMISSION_REFERENCE_PREFIX_MAX} characters.`;
  }
  if (prefix && !/^[A-Za-z0-9_-]*$/.test(prefix)) {
    return 'Prefix may only contain letters, numbers, underscores, and hyphens.';
  }

  if (config.submissionReferenceMode !== 'custom') {
    return null;
  }

  const pattern = config.submissionReferencePattern.trim();
  if (!pattern) {
    return 'Enter a custom reference pattern.';
  }
  if (pattern.length > SUBMISSION_REFERENCE_PATTERN_MAX) {
    return `Pattern must be at most ${SUBMISSION_REFERENCE_PATTERN_MAX} characters.`;
  }
  if (!hasValidSubmissionReferenceSeqToken(pattern)) {
    return 'Custom pattern must include exactly one sequence token (Seq 4–8).';
  }
  return null;
}

export function draftRetentionHoursFromConfig(
  value: FormSubmissionDraftRetentionOption,
): number {
  const hours = Number.parseInt(value, 10);
  if (hours === 0) {
    return 0;
  }
  if (![24, 48, 168, 336, 720].includes(hours)) {
    return DEFAULT_FORM_SUBMISSION_DRAFT_RETENTION_HOURS;
  }
  return hours;
}

export function draftRetentionOptionFromHours(
  hours: number | null | undefined,
): FormSubmissionDraftRetentionOption {
  if (hours === 0) {
    return '0';
  }
  if (hours == null) {
    return String(
      DEFAULT_FORM_SUBMISSION_DRAFT_RETENTION_HOURS,
    ) as FormSubmissionDraftRetentionOption;
  }
  const asString = String(hours) as FormSubmissionDraftRetentionOption;
  const allowed: FormSubmissionDraftRetentionOption[] = [
    '0',
    '24',
    '48',
    '168',
    '336',
    '720',
  ];
  return allowed.includes(asString)
    ? asString
    : (String(
        DEFAULT_FORM_SUBMISSION_DRAFT_RETENTION_HOURS,
      ) as FormSubmissionDraftRetentionOption);
}

export function draftRetentionLabel(
  hours: number | null | undefined,
): string {
  if (hours === 0) {
    return 'Drafts: off';
  }
  const option = draftRetentionOptionFromHours(hours);
  return (
    FORM_SUBMISSION_DRAFT_RETENTION_OPTIONS.find((o) => o.value === option)
      ?.label ?? '24 hours'
  );
}

export function isDraftsDisabled(
  hours: number | null | undefined,
): boolean {
  return hours === 0;
}

const FORM_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const FORM_SLUG_MIN = 2;
const FORM_SLUG_MAX = 64;
const FALLBACK_APP_ORIGIN = 'https://forms.formops.app';

export function currentAppOrigin(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/\/$/, '');
  }
  return FALLBACK_APP_ORIGIN;
}

export function createDefaultFormPublishConfig(): FormPublishConfig {
  return {
    customSlug: '',
    visibility: 'public',
    accessPassword: '',
    allowedContactIds: [],
    allowedGroupIds: [],
    invitedEmails: [],
    publishTiming: 'immediate',
    scheduledAt: '',
    requireSubmissionApproval: false,
    closeOnScheduleEnd: false,
    scheduledEndAt: '',
    maxSubmissions: '',
    formSubmissionDraftRetentionHours: String(
      DEFAULT_FORM_SUBMISSION_DRAFT_RETENTION_HOURS,
    ) as FormSubmissionDraftRetentionOption,
    submissionReferenceMode: 'standard',
    submissionReferencePrefix: 'REF',
    submissionReferenceDigitLength: DEFAULT_SUBMISSION_REFERENCE_DIGIT_LENGTH,
    submissionReferencePattern: DEFAULT_SUBMISSION_REFERENCE_PATTERN,
  };
}

export function normalizeFormSlug(value: string): string {
  return value
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1-$2')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, FORM_SLUG_MAX);
}

export function isValidFormSlug(value: string): boolean {
  const slug = value.trim();
  return (
    slug.length >= FORM_SLUG_MIN &&
    slug.length <= FORM_SLUG_MAX &&
    FORM_SLUG_PATTERN.test(slug)
  );
}

export function buildPublishedFormUrl(
  workspaceSlug: string,
  formSlug: string,
  origin = currentAppOrigin(),
): string {
  const ws = normalizeFormSlug(workspaceSlug) || 'workspace';
  const form = normalizeFormSlug(formSlug) || 'untitled';
  return `${origin.replace(/\/$/, '')}/${ws}/${form}`;
}

export function buildPublishedFormHostPath(
  workspaceSlug: string,
  formSlug: string,
  host = currentAppOrigin().replace(/^https?:\/\//, ''),
): string {
  const ws = normalizeFormSlug(workspaceSlug) || 'workspace';
  const form = normalizeFormSlug(formSlug) || '…';
  return `${host}/${ws}/${form}`;
}

export function buildPublishedFormAbsoluteUrl(
  workspaceSlug: string,
  formSlug: string,
  origin = currentAppOrigin(),
): string {
  return buildPublishedFormUrl(workspaceSlug, formSlug, origin);
}

export function buildShortFormUrl(
  shortCode: string,
  origin = currentAppOrigin(),
): string {
  const code = shortCode.trim();
  if (!code) {
    return '';
  }
  return `${origin.replace(/\/$/, '')}/f/${code}`;
}

export function shortFormHostPath(shortCode: string): string {
  const code = shortCode.trim();
  if (!code) {
    return '';
  }
  const host = currentAppOrigin().replace(/^https?:\/\//, '');
  return `${host}/f/${code}`;
}

export function buildEmbedScriptUrl(origin = currentAppOrigin()): string {
  return `${origin.replace(/\/$/, '')}/embed.js`;
}

export function buildEmbedIframe(
  workspaceSlug: string,
  formSlug: string,
): string {
  const url = buildPublishedFormUrl(workspaceSlug, formSlug);
  return [
    '<iframe',
    `  src="${url}"`,
    '  style="width:100%;min-height:600px;border:0;"',
    '  title="FormOps form">',
    '</iframe>',
  ].join('\n');
}

export function buildEmbedScript(
  workspaceSlug: string,
  formSlug: string,
): string {
  const ws = normalizeFormSlug(workspaceSlug) || 'workspace';
  const form = normalizeFormSlug(formSlug) || 'untitled';
  const embedSrc = buildEmbedScriptUrl();
  return [
    '<script',
    `  src="${embedSrc}"`,
    `  data-workspace="${ws}"`,
    `  data-form="${form}"`,
    '  async>',
    '</script>',
  ].join('\n');
}

export function buildEmbedWebComponent(
  workspaceSlug: string,
  formSlug: string,
): string {
  const ws = normalizeFormSlug(workspaceSlug) || 'workspace';
  const form = normalizeFormSlug(formSlug) || 'untitled';
  const embedSrc = buildEmbedScriptUrl();
  return [
    '<script',
    `  src="${embedSrc}"`,
    '  async>',
    '</script>',
    '<formops-form',
    `  data-workspace="${ws}"`,
    `  data-form="${form}">`,
    '</formops-form>',
  ].join('\n');
}
